import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, concatMap, debounceTime, distinctUntilChanged, flatMap, map, tap } from 'rxjs/operators';
import {
    ChapterType,
    ICellChange,
    ICellChangeResponse,
    ICellOutput,
    IDetailPriceTraveller,
    IQuotationBlock,
    IQuotationCost,
    IQuotationPriceTotal,
    IQuotationSection,
    IQuotationsQuotation,
    IQuotationTraveller,
    QuotationCellsActions,
} from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import { CostTableDataService } from "../../services/cost-table-data/cost-table-data.service";
import { ITableBlock, ITableHeaders, ITableRow, TableRowType } from "app_quotation/shared/services/table-data/ITable";
import { Yii } from "libraries/interfaces/Yii";
import { ConfigurationResourceService } from "libraries/service/resources/configuration/configurationResource.service";
import { ILabels } from "libraries/interfaces/ILabels";
import {
    IQuotationQuotationsBlockPatch,
    IQuotationQuotationsBlocksCreate,
    IQuotationQuotationsSectionsCreate,
    IQuotationsSuppliersCreateInput,
    IQuotationsSuppliersSearch,
    QuotationQuotationsResourceService
} from 'libraries/service/resources/quotation-quotations/quotation-quotations-resource.service';
import {
    CurrencyListResourceService,
    ICurrencyOption
} from "libraries/service/resources/currency-list/currency-list-resource.service";
import { PermissionResourceService } from 'libraries/service/resources/permission/permissionResource.service';
import { ErrorTracingService } from 'libraries/service/error-tracing/error-tracing.service';
import {
    ICoordsTable,
    TableKeyboardNavigationService
} from 'libraries/service/table-keyboard-navigation/table-keyboard-navigation.service';
import { CachedQuotationService } from 'app_quotation/shared/services/cached-quotation.service';
import { ActivatedRoute, ParamMap } from "@angular/router";
import { EHosts } from "libraries/enums/EHosts";
import { ERoute } from "app_quotation/shared/enums/ERoute";
import _cloneDeep from 'lodash-es/cloneDeep';
import { QuotationPriceToolsService } from "app_quotation/shared/pipes/quotation-price-tools.service";
import { EMoveMode } from "libraries/enums/e-move-mode";
import { ConfirmDialogComponent } from 'libraries/component/dialogs/confirm-dialog/confirm-dialog.component';
import { IConfirmDialogData } from 'libraries/component/dialogs/confirm-dialog/confirm-dialog.interface';
import { LabelService } from 'libraries/service/label/label.service';
import { wenji } from 'libraries/interfaces/IWenji';
import { QuotationBlockCategoryCodeEnum } from "libraries/interfaces/quotation-block-category-code.enum";
import { EQuotationEntryPoints } from 'libraries/enums/e-quotation-entry-points';


export interface ITableBlockCategoryConfig {
    code: QuotationBlockCategoryCodeEnum;
    icon: string;
    wording: string;
}

@Component({
    selector: 'cost-grid',
    templateUrl: './cost-grid.component.html',
    styleUrls: ["./cost-grid.component.scss", "../../../../shared/styles/quotation-grid.scss"],
})
export class CostGridComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    @Input()
    public quotation: IQuotationsQuotation;

    @Output()
    public readonly isXRateNoValid: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onError: EventEmitter<string> = new EventEmitter<string>();

    @Output()
    public readonly quotationChange: EventEmitter<IQuotationsQuotation> = new EventEmitter<IQuotationsQuotation>();

    public tableData: ITableRow[];

    public tableHeaders: ITableHeaders;
    public displayedColumns: string[];

    public colspan: number = 4;
    public totalColspan: number = 2;
    public chapterColspan: number;

    public focusCoords: ICoordsTable;
    public focusCoordsFromKeyboard: ICoordsTable;
    public tableRowType: typeof TableRowType = TableRowType;
    public rowIndexSubtract: number[];
    public colMax: number;
    public rowMax: number;
    public moveMode: EMoveMode = EMoveMode.grid;

    public costBlockCategoryConfiguration: Map<string, ITableBlockCategoryConfig>;

    public defaultCurrencies: ICurrencyOption[] = [];

    public wording: ILabels = {
        host: Yii.t('wui-quotation-js', 'Fournisseur'),
        total: Yii.t('wui-quotation-js', 'Total'),
        currency: Yii.t('wui-quotation-js', 'Devise'),
    };

    public quotationCurrency: string = "EUR";
    public suppliers: IQuotationsSuppliersSearch[] = [];

    public areVisibleBlocks: { [key: string]: boolean } = {
        [ChapterType.trip]: true,
        [ChapterType.fees]: true,
        [ChapterType.services]: true,
    };

    public subPage: EQuotationEntryPoints = EQuotationEntryPoints.included;

    // Permission to edit, by default=true because it causes issues if the values changes from false to true
    public hasPermissionToEdit: boolean = true;

    private readonly defaultCostBlockCategoryConfig: ITableBlockCategoryConfig[] = [
        {
            code: QuotationBlockCategoryCodeEnum.accomodation,
            icon: 'fa fa-bed',
            wording: Yii.t('wui-quotation-js', 'Hébergement')
        },
        {
            code: QuotationBlockCategoryCodeEnum.transportation,
            icon: 'fa fa-plane',
            wording: Yii.t('wui-quotation-js', 'Transport')
        },
        {
            code: QuotationBlockCategoryCodeEnum.price,
            icon: 'fa fa-dollar',
            wording: Yii.t('wui-quotation-js', 'Prix')
        },
    ];

    private readonly cellChanges: Subject<ICellChange> = new Subject<ICellChange>();
    private searchSuppliersSubject: Subject<{ name: string, category: string }> = new Subject<{ name: string, category: string }>();
    private searchSuppliersSubscription: Subscription;

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly activatedRoute: ActivatedRoute,
        private readonly cachedQuotationService: CachedQuotationService,
        private readonly configurationResourceService: ConfigurationResourceService,
        private readonly costTableDataService: CostTableDataService,
        private readonly currencyListResource: CurrencyListResourceService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly permissionResource: PermissionResourceService,
        private readonly quotationQuotationsResourceService: QuotationQuotationsResourceService,
        private readonly tableKeyboardNavigationService: TableKeyboardNavigationService,
        private readonly priceToolsService: QuotationPriceToolsService,
        private readonly dialog: MatDialog,
        private readonly labelService: LabelService,
    ) {
    }

    public ngOnInit(): void {
        this.configurationResourceService
            .get(['wui', 'app_quotation', 'config', 'json-configuration', 'block-category.json'], this.defaultCostBlockCategoryConfig)
            .subscribe((config: ITableBlockCategoryConfig[]) => {
                this.costBlockCategoryConfiguration = new Map<string, ITableBlockCategoryConfig>(
                    config.map((item: ITableBlockCategoryConfig) => [item.code.toLowerCase(), item] as [string, ITableBlockCategoryConfig])
                );
            });

        this.activatedRoute.paramMap.subscribe((parameters: ParamMap) => {
            this.subPage = parameters.get(ERoute.subPage) as EQuotationEntryPoints;
            this.initGrid(this.quotation);
        });

        this.quotationCurrency = this.quotation.cost && this.quotation.cost.currency ? this.quotation.cost.currency : "EUR";

        this.currencyListResource.getCurrencyOption(this.quotationCurrency)
            .subscribe((currencies: ICurrencyOption[]): void => {
                this.defaultCurrencies = currencies;
            });

        /*
        this.quotationQuotationsResourceService.getQuotationSuppliers()
            .subscribe({
                next: (options: IQuotationsSuppliersSearch[]): void => {
                    this.suppliers = options;
                }
            });
        */
        this.cellChanges
            .pipe(
                // it's important to use concatMap here to avoid parallel execution of requests
                concatMap((cellChange: ICellChange): Observable<ICellChangeResponse> => {
                    return this.handleCellChange(cellChange)
                        .pipe(
                            // Errors are handled at this point to avoid blocking the whole
                            // UI if an error occurs somewhere (for instance, in the API)
                            catchError((error: Error): Observable<ICellChangeResponse> => {
                                this.errorTracingService.captureException(error);
                                return of(undefined);
                            })
                        );
                })
            )
            .subscribe(
                {
                    next: (cellChangeResponse: ICellChangeResponse | undefined): void => {
                        if (cellChangeResponse) {
                            this.updateAfterCellChange(cellChangeResponse);
                            if (cellChangeResponse.quotation) {
                                this.quotationChange.emit(cellChangeResponse.quotation);
                            }
                            if (cellChangeResponse.quotation && !cellChangeResponse.quotation.isXRateValid) {
                                this.isXRateNoValid.emit();
                            }
                        } else {
                            this.hasPermissionToEdit = false;
                            this.onError.emit(Yii.t('wui-shared-js', 'Problème réseau rencontré. Veuillez rafraîchir la page.'));
                        }

                    },
                    error: (error: Error): void => {
                        this.errorTracingService.captureException(error);
                    }
                });

        this.permissionResource.hasPermission('quotation.cost.edit')
            .subscribe({
                next: (hasPermission: boolean): void => {
                    this.hasPermissionToEdit = hasPermission;
                },
                error: (error: Error): void => {
                    this.hasPermissionToEdit = false;
                    this.errorTracingService.captureException(error);
                }
            });
        this.searchSuppliersSubscription = this.searchSuppliersSubject.pipe(
            debounceTime(1000),
            distinctUntilChanged())
            .subscribe((search: { name: string, category: string }): void => {
                this.searchSuppliers(search);
            });
    }

    public ngAfterViewInit(): void {
        this.initGridStyles();
    }

    public ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges.quotation &&
            simpleChanges.quotation.currentValue &&
            !simpleChanges.quotation.isFirstChange()) {
            this.initGrid(simpleChanges.quotation.currentValue);
            this.initGridStyles();
        }
    }

    public ngOnDestroy(): void {
        this.cellChanges.complete();
        this.searchSuppliersSubscription.unsubscribe();
    }

    public initGridStyles(): void {
        const offsetHeight: number = this.document.getElementById('table-head').offsetHeight;
        this.document.getElementById('row0').style.top = `${offsetHeight}px`;
    }

    public initGrid(quotation: IQuotationsQuotation): void {
        this.tableData = this.costTableDataService.computeTableData(quotation, this.subPage);
        this.tableHeaders = this.costTableDataService.computeTableHeaders(quotation, this.subPage);
        this.displayedColumns = [...Object.keys(this.tableHeaders), 'end'];
        this.chapterColspan = 2;
        if (quotation.travellers) {
            this.chapterColspan += quotation.travellers.length;
        }
        this.createRowIndex(this.tableData);
        this.quotationCurrency = quotation.cost && quotation.cost.currency ? quotation.cost.currency : "EUR";

    }

    public emptyAndFocus(newCoords: ICoordsTable): void {
        this.focusCoords = newCoords;
    }

    public createRowIndex(tableData: ITableRow[]): void {
        let substract: number = 0;

        this.rowIndexSubtract = tableData.map((data: ITableRow, index: number): number => {
            if (this.isHeader(data.type) || (!this.isEditableCell(data) && !data.block.isEmpty)) {
                substract++;
            }

            return index - substract;
        });

        this.setTableBoundaries();
    }

    public addPlaceholderAfter(element: ITableRow, rowIndex: number): void {
        const table: ITableRow[] = this.costTableDataService.getTableAfterAddPriceBlockPlaceholder(this.tableData, element);
        this.tableData = [...table];
        this.createRowIndex(this.tableData);
        this.focusCoords = {
            rowIndex: rowIndex + 1,
            colIndex: -1,
        };
    }

    public isEditableCell(row: ITableRow): boolean {
        if (!this.hasPermissionToEdit || this.quotation.isReadOnly) {
            return false;
        }

        if (row.block && row.block.isEmpty) {
            return true;
        }

        if (row.block && row.block.totalPrices && row.block.totalPrices[0].externalReference) {
            return row.block.totalPrices[0].externalReference.host === EHosts.WATABI;
        }

        return true;
    }

    public isHeader(rowType: TableRowType): boolean {
        return [TableRowType.totalHeader, TableRowType.adjustmentHeader, TableRowType.chapterHeader].includes(rowType);
    }

    public isBlock(rowType: TableRowType): boolean {
        return Boolean(rowType === this.tableRowType.blockData);
    }

    public trackTraveller(index: number, traveller: IQuotationTraveller): number {
        return traveller.id;
    }

    public updateSupplier({supplierId, id, cost, itemType}: ICellOutput): Observable<ICellChangeResponse> {
        return this.cachedQuotationService.patchQuotationBlock(
            this.quotation.id,
            id,
            {supplier: {id: supplierId}}
        ).pipe(
            flatMap((quotation: IQuotationsQuotation): Observable<ICellChangeResponse> => this.updateCurrencyIfNecessary(quotation, {
                id,
                cost,
                itemType
            })),
        );
    }

    public noCreateButUpdateSupplier({name, id, cost, itemType}: ICellOutput): Observable<ICellChangeResponse> {
        return this.quotationQuotationsResourceService.patchBlock(
            {supplier: {name}},
            id,
        ).pipe(
            flatMap((quotation: IQuotationsQuotation): Observable<ICellChangeResponse> => this.updateCurrencyIfNecessary(quotation, {
                    id,
                    cost,
                    itemType,
                })
            )
        );
    }

    public createSupplier(cellOutput: ICellOutput): Observable<ICellChangeResponse> {
        const dialogData: IConfirmDialogData = {
            title: Yii.t("wui-quotation-js", "Création d'un fournisseur"),
            question: this.labelService.completeLabel(
                Yii.t("wui-quotation-js", "Voulez-vous créer le fournisseur %placeholder% ?"),
                cellOutput.name
            ),
            accept: Yii.t("wui-shared-js", "Créer"),
            cancel: Yii.t("wui-shared-js", "Annuler"),
        };

        const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
            height: "fit-content",
            maxWidth: "800px",
            autoFocus: true,
            data: dialogData
        });

        return dialogRef.afterClosed()
            .pipe(
                flatMap((response: string) => this.cachedQuotationService.get(this.quotation.id).pipe(
                    map((cachedQuotation: IQuotationsQuotation) => ({cachedQuotation, response}))
                )),
                flatMap(({
                             cachedQuotation,
                             response
                         }: { cachedQuotation: IQuotationsQuotation, response: string }): Observable<ICellChangeResponse> => {
                    if (!response) {
                        return this.quotationQuotationsResourceService.patchBlock(
                            {supplier: {name: cellOutput.name}},
                            cellOutput.id,
                        ).pipe(
                            flatMap((quotation: IQuotationsQuotation): Observable<ICellChangeResponse> => this.updateCurrencyIfNecessary(quotation, {
                                    id: cellOutput.id,
                                    cost: cellOutput.cost,
                                    itemType: cellOutput.itemType,
                                })
                            )
                        );
                    }

                    const createSupplierInput: IQuotationsSuppliersCreateInput = {
                        name: cellOutput.name,
                        preferredCurrency: cellOutput.preferredCurrency,
                        currencies: cellOutput.currencies,
                        categories: cellOutput.categories,
                    };
                    return this.quotationQuotationsResourceService.createSupplier(createSupplierInput).pipe(
                        tap((supplier: IQuotationsSuppliersSearch): void => {
                            this.suppliers = [...(this.suppliers || []), supplier];
                        }),
                        flatMap((supplier: IQuotationsSuppliersSearch): Observable<IQuotationsQuotation> => {
                            return this.cachedQuotationService.patchQuotationBlock(
                                this.quotation.id,
                                cellOutput.id, {
                                    supplier: {id: supplier.id}
                                });
                        }),
                        flatMap((quotation: IQuotationsQuotation): Observable<ICellChangeResponse> =>
                            this.updateCurrencyIfNecessary(
                                quotation,
                                {
                                    id: cellOutput.id,
                                    cost: {
                                        currency: cellOutput.preferredCurrency
                                    },
                                    itemType: cellOutput.itemType
                                }
                            )),
                    );
                })
            );
    }

    public updatePriceBlocTitle({title, id, itemType}: ICellOutput): Observable<ICellChangeResponse> {
        const request: IQuotationQuotationsBlockPatch = {
            title
        };
        return this.cachedQuotationService
            .patchBlock(this.quotation.id, request, id)
            .pipe(
                map((quotation: IQuotationsQuotation): ICellChangeResponse => ({quotation, itemType}))
            );
    }

    public createPriceBlock({title, chapterCode, itemType}: ICellOutput): Observable<ICellChangeResponse> {
        const sectionId: number = this.costTableDataService.getSectionId(this.quotation, chapterCode);
        const request: IQuotationQuotationsBlocksCreate = this.getBlockRequest(title, this.subPage === EQuotationEntryPoints.option);
        const previousFocusCoords: ICoordsTable = {...this.focusCoords};

        const result: Observable<ICellChangeResponse> = sectionId > 0
            ? this.cachedQuotationService
                .createBlock(this.quotation.id, request, sectionId.toString())
                .pipe(
                    map((response: IQuotationBlock): ICellChangeResponse => ({
                        newBlock: response,
                        chapterCode,
                        itemType
                    }))
                )
            : this.createFirstPriceBlock({title, chapterCode, itemType});

        return result.pipe(
            tap((): void => {
                this.focusCoords = {
                    rowIndex: previousFocusCoords.rowIndex,
                    colIndex: 0,
                };
            })
        );
    }

    public createFirstPriceBlock({title, chapterCode, itemType}: ICellOutput): Observable<ICellChangeResponse> {
        const section: IQuotationQuotationsSectionsCreate = {
            title: Yii.t("wui-watabi-js", "Devis terrestre"),
            chapterCode,
            blocks: [
                this.getBlockRequest(title, this.subPage === EQuotationEntryPoints.option),
            ]
        };

        return this.cachedQuotationService
            .createSection(section, this.quotation.id)
            .pipe(
                map((response: IQuotationSection): ICellChangeResponse => ({
                    newBlock: response.blocks[0],
                    chapterCode,
                    itemType
                }))
            );
    }

    public removePriceBlock({id, itemType}: ICellOutput): Observable<ICellChangeResponse> {
        return this.cachedQuotationService.deleteBlock(this.quotation.id, id)
            .pipe(
                flatMap((): Observable<IQuotationsQuotation> => this.cachedQuotationService.get(this.quotation.id)),
                map((quotation: IQuotationsQuotation): ICellChangeResponse => ({
                    itemType,
                    blockId: id,
                    quotation
                }))
            );
    }

    public emitTravellerDetailPriceChange(cost: IQuotationCost, travellerId: number, block: IQuotationBlock): void {
        const id: number = extractTravellerDetailId(this.extractTravellers(block), travellerId);
        this.emitCellChange(
            {
                id,
                travellerId,
                itemType: QuotationCellsActions.updateDetailPriceTraveller,
                cost
            },
            block.id);
    }

    public emitCellChange(output: ICellOutput, blockId?: number): void {
        this.cellChanges.next({output, blockId});
    }

    public emitFocusChange(coords: ICoordsTable): void {
        this.focusCoords = coords;
    }

    public onKeyup(keyEvent: KeyboardEvent): void {
        if ((this.moveMode === EMoveMode.grid || this.moveMode === EMoveMode.cell) && this.focusCoords) {
            switch (keyEvent.key) {
                case ("ArrowLeft"):
                case ("Left"):
                    if (this.focusCoords.colIndex === -1) {
                        this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveLeft(
                            {
                                rowIndex: this.focusCoords.rowIndex,
                                colIndex: 0,
                            },
                            this.colMax,
                        );
                        this.focusCoords = this.focusCoordsFromKeyboard;
                        break;
                    }
                    this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveLeft(this.focusCoords, this.colMax);
                    this.focusCoords = this.focusCoordsFromKeyboard;
                    break;

                case ("ArrowRight"):
                case ("Right"):
                    if (this.focusCoords.colIndex === -1) {
                        this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveRight(
                            {
                                rowIndex: this.focusCoords.rowIndex,
                                colIndex: this.colMax,
                            },
                            this.rowMax,
                            this.colMax,
                        );
                        this.focusCoords = this.focusCoordsFromKeyboard;
                        break;
                    }
                    this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveRight(this.focusCoords, this.rowMax, this.colMax);
                    this.focusCoords = this.focusCoordsFromKeyboard;
                    break;

                case ("ArrowUp"):
                case ("Up"):
                    if (this.focusCoords.colIndex === -1) {
                        this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveUp(
                            {
                                rowIndex: this.focusCoords.rowIndex,
                                colIndex: 0,
                            },
                        );
                        this.focusCoords = this.focusCoordsFromKeyboard;
                        break;
                    }
                    this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveUp(this.focusCoords);
                    this.focusCoords = this.focusCoordsFromKeyboard;
                    break;

                case ("ArrowDown"):
                case ("Down"):
                    if (this.focusCoords.colIndex === -1) {
                        this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveDown(
                            {
                                rowIndex: this.focusCoords.rowIndex,
                                colIndex: 0,
                            },
                            this.rowMax,
                        );
                        this.focusCoords = this.focusCoordsFromKeyboard;
                        break;
                    }
                    this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveDown(this.focusCoords, this.rowMax);
                    this.focusCoords = this.focusCoordsFromKeyboard;
                    break;

                default:
                    break;
            }
        }

        if ((this.moveMode === EMoveMode.cell || this.moveMode === EMoveMode.insideCell) && keyEvent.key === "Enter") {
            if (this.moveMode === EMoveMode.cell) {
                this.moveMode = EMoveMode.insideCell;
            } else {
                this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveNextLine(this.focusCoords, this.rowMax, this.colMax);
                this.focusCoords = this.focusCoordsFromKeyboard;
                this.moveMode = EMoveMode.grid;
            }
        }
    }

    public onMoveModeChange(moveMode: EMoveMode): void {
        this.moveMode = moveMode;
    }

    public collapse(chapterType: ChapterType, isVisible: boolean): void {
        this.areVisibleBlocks[chapterType] = isVisible;
    }

    public onSearchSuppliers(search: { name: string, category: string }): void {
        this.searchSuppliersSubject.next(search);
    }

    private searchSuppliers(search: { name: string, category: string }): void {
        this.quotationQuotationsResourceService.searchQuotationSuppliers(search)
            .subscribe({
                next: (options: IQuotationsSuppliersSearch[]): void => {
                    this.suppliers = options;
                }
            });
    }

    private extractTravellers(block: IQuotationBlock): IDetailPriceTraveller[] {
        return this.priceToolsService.hasTotalPrice(block) && Boolean(block.totalPrices[0].details) ?
            block.totalPrices[0].details[0].travellers :
            null;
    }

    private handleCellChange(cellChange: ICellChange): Observable<ICellChangeResponse> {
        // tslint:disable-next-line:switch-default
        switch (cellChange.output.itemType) {
            case QuotationCellsActions.updatePriceTotalCurrency:
                return this.updateTotalPriceCurrency(cellChange);
            case QuotationCellsActions.updateDetailPriceTraveller:
                return this.updateTravellerDetailPrice(cellChange);
            case QuotationCellsActions.updateDetailPrice:
                return this.updatePriceBlockDetailCost(cellChange);
            case QuotationCellsActions.updatePriceBlocTitle:
                return this.updatePriceBlocTitle(cellChange.output);
            case QuotationCellsActions.createPriceBlock:
                return this.createPriceBlock(cellChange.output);
            case QuotationCellsActions.removePriceBlock:
                return this.removePriceBlock(cellChange.output);
            case QuotationCellsActions.updateSupplier:
                return this.updateSupplier(cellChange.output);
            case QuotationCellsActions.createSupplier:
                if (wenji.quotation.createSupplier === "1") {
                    return this.createSupplier(cellChange.output);
                }
                return this.noCreateButUpdateSupplier(cellChange.output);
        }
    }

    private updateAfterCellChange(cellChangeResponse: ICellChangeResponse | undefined): void {
        switch (cellChangeResponse && cellChangeResponse.itemType) {
            case QuotationCellsActions.createPriceBlock:
                this.tableData = this.costTableDataService.getTableAfterCreatePriceBlock(
                    this.tableData,
                    cellChangeResponse.newBlock,
                    cellChangeResponse.chapterCode
                );
                this.createRowIndex(this.tableData);
                this.focusCoords = {
                    ...this.focusCoords
                };
                break;
            case QuotationCellsActions.removePriceBlock:
                this.quotation = cellChangeResponse.quotation || this.quotation;
                this.tableData = this.costTableDataService.getTableAfterRemovePriceBlock(this.tableData, cellChangeResponse, this.quotation);
                this.createRowIndex(this.tableData);
                this.focusCoords = {
                    ...this.focusCoords,
                    rowIndex: this.focusCoords.rowIndex - 1
                };
                this.refreshAfterUpdate(cellChangeResponse);
                break;
            default:
                this.refreshAfterUpdate(cellChangeResponse);
                break;
        }
    }

    private updateCurrencyIfNecessary(quotation: IQuotationsQuotation, {
        id,
        cost,
        itemType
    }: ICellOutput): Observable<ICellChangeResponse> {
        const blocks: IQuotationBlock[] = quotation.sections.reduce(
            (list: IQuotationBlock[], section: IQuotationSection): IQuotationBlock[] => [...list, ...(section.blocks || [])],
            []
        );

        const updatedBlock: IQuotationBlock = blocks.find((block: IQuotationBlock): boolean => block.id === id);
        const mustUpdateCurrency: boolean = updatedBlock && (updatedBlock.totalPrices || [])
            .some((totalPrice: IQuotationPriceTotal): boolean => cost && totalPrice.cost.currency !== cost.currency);

        if (mustUpdateCurrency) {
            return this.updateTotalPriceCurrency({
                blockId: id,
                output: {
                    cost,
                    itemType,
                }
            });
        }

        return of({
            quotation,
            blockId: id,
            itemType,
        });
    }

    private setTableBoundaries(): void {
        this.colMax = (this.quotation.travellers || []).length + 2;
        this.rowMax = this.rowIndexSubtract[this.rowIndexSubtract.length - 1];
    }

    private refreshAfterUpdate(cellChangeResponse: ICellChangeResponse): void {
        if (cellChangeResponse) {

            const relatedRow: ITableRow = this.tableData
                .filter((row: ITableRow): boolean => this.isBlock(row.type))
                .find((row: ITableRow): boolean => row.block.id === cellChangeResponse.blockId);

            if (relatedRow) {
                const blockToUpdate: ITableBlock = relatedRow.block;

                //update traveller total Cost
                if (this.subPage === EQuotationEntryPoints.included) {
                    //update traveller total Cost
                    this.updateTravellersCost(cellChangeResponse.quotation.travellers);

                    //update total Cost
                    this.changeTotalCost(cellChangeResponse.quotation.cost);
                }

                const sectionId: number = blockToUpdate.section;

                const newBlock: IQuotationBlock = cellChangeResponse.quotation.sections
                    .find((section: IQuotationSection): boolean => section.id === sectionId)
                    .blocks
                    .find((block: IQuotationBlock): boolean => block.id === cellChangeResponse.blockId);

                if ((blockToUpdate.supplier && newBlock.supplier && blockToUpdate.supplier.id !== newBlock.supplier.id)
                    || (blockToUpdate.supplier && !newBlock.supplier)
                    || (!blockToUpdate.supplier && newBlock.supplier)
                    || cellChangeResponse.itemType === QuotationCellsActions.refreshBlock
                    || cellChangeResponse.itemType === QuotationCellsActions.createSupplier
                ) {
                    blockToUpdate.supplier = newBlock.supplier;
                    if (newBlock.supplier?.preferredCurrency) {
                        newBlock.totalPrices[0].cost.currency = newBlock.supplier.preferredCurrency;
                    }
                }

                //copy the original Block, then update it
                const tableBlock: IQuotationBlock = _cloneDeep(blockToUpdate);
                changeBlockCost(tableBlock, newBlock.totalPrices[0].cost);
                changeBlockTravellers(tableBlock, newBlock);

                //inject the updated block in tableRow
                this.tableData
                    .filter((row: ITableRow): boolean => this.isBlock(row.type))
                    .find((row: ITableRow): boolean => row.block.id === cellChangeResponse.blockId).block = tableBlock;
            }

        }
    }

    private changeTotalCost(cost: IQuotationCost): void {
        this.tableData[0].total = cost;
    }

    private updateTravellerDetailPrice({blockId, output}: ICellChange): Observable<ICellChangeResponse> {
        return this.cachedQuotationService.patchDetailPriceTravellerCostReturn(this.quotation.id, output.id, output.cost)
            .pipe(
                map((quotation: IQuotationsQuotation): ICellChangeResponse => ({
                    quotation,
                    blockId,
                    itemType: output.itemType
                }))
            );
    }

    private updateTotalPriceCurrency({blockId, output}: ICellChange): Observable<ICellChangeResponse> {
        const totalPriceId: number = this.tableData
            .find((row: ITableRow): boolean => row.block && row.block.id === blockId)
            .block.totalPrices[0].id;

        return this.cachedQuotationService.patchTotalPriceCurrency(this.quotation.id, totalPriceId, output.cost)
            .pipe(
                map((quotation: IQuotationsQuotation): ICellChangeResponse => ({
                    quotation,
                    blockId,
                    itemType: output.itemType
                }))
            );
    }

    private updatePriceBlockDetailCost({blockId, output}: ICellChange): Observable<ICellChangeResponse> {
        const id: number = this.tableData
            .filter((row: ITableRow): boolean => this.isBlock(row.type))
            .find((row: ITableRow): boolean => row.block.id === blockId)
            .block.totalPrices[0].details[0].id;
        return this.cachedQuotationService.patchBlockDetailPrice(this.quotation.id, id, output.cost)
            .pipe(
                map((quotation: IQuotationsQuotation): ICellChangeResponse => ({
                    quotation,
                    blockId,
                    itemType: output.itemType
                }))
            );
    }

    private updateTravellersCost(newTravellers: IQuotationTraveller[]): void {
        this.tableData[0].travellers
            .forEach((referenceTraveller: IQuotationTraveller): void => {
                referenceTraveller.cost = newTravellers.find((newTraveller: IQuotationTraveller): boolean => newTraveller.id === referenceTraveller.id).cost;
            });
    }

    private getBlockRequest(title: string, isOption: boolean): IQuotationQuotationsBlocksCreate {
        return {
            category: "Price",
            isOption,
            title,
            hasCommission: Boolean(this.quotation?.retail?.commission),
            totalPrices: [{
                cost: {
                    amount: null,
                    currency: this.quotationCurrency || "EUR"
                }
            }]
        };
    }
}

function changeBlockCost(blockToUpdate: ITableBlock, cost: IQuotationCost): void {
    blockToUpdate.cost = cost;
    blockToUpdate.totalPrices[0].cost = cost;
    blockToUpdate.totalPrices[0].details[0].cost = cost;
}

function changeBlockTravellers(blockToUpdate: ITableBlock, newBlock: IQuotationBlock): void {
    blockToUpdate.totalPrices = newBlock.totalPrices;
    blockToUpdate.travellerPrices = newBlock.travellerPrices;
}

function extractTravellerDetailId(detailPriceTravellers: IDetailPriceTraveller[], travellerId: number): number {
    if (detailPriceTravellers) {
        return detailPriceTravellers
            .find((traveller: IDetailPriceTraveller) => traveller.traveller === travellerId)
            .id;
    }
    return null;
    // tslint:disable-next-line:max-file-line-count
}
